/**
 * Module dependencies.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from 'app/components/ui/badge';
import { formatCurrency } from 'app/core/utils/formatter';
import { PiValuationHolding, usePoolInvestValuation } from 'app/hooks/requests/pool-invest/use-pool-invest';
import { cn } from 'app/lib/utils';

/**
 * Format a crypto quantity compactly (up to 8 dp, no trailing zeros).
 */

function fmtQty(value: string): string {
  const n = parseFloat(value);

  return Number.isFinite(n) ? String(parseFloat(n.toFixed(8))) : '0';
}

/**
 * Render a participant's frozen holdings (one line per asset).
 */

function Holdings({ holdings }: { holdings: PiValuationHolding[] }): JSX.Element {
  if (!holdings.length) {
    return <span className={'text-muted-foreground'}>{'—'}</span>;
  }

  return (
    <span className={'flex flex-col items-end gap-0.5'}>
      {holdings.map(holding => (
        <span className={'tabular-nums'} key={holding.symbol}>
          {fmtQty(holding.quantity)} <span className={'text-xs text-muted-foreground'}>{holding.symbol}</span>
        </span>
      ))}
    </span>
  );
}

/**
 * Signed euro + percentage, coloured by sign.
 */

function Profit({ value, pct }: { value: number; pct: number | null }): JSX.Element {
  const cls = value > 0 ? 'text-emerald-600' : value < 0 ? 'text-destructive' : 'text-muted-foreground';

  return (
    <span className={cn('tabular-nums', cls)}>
      {value > 0 ? '+' : ''}
      {formatCurrency(value)}
      {pct !== null ? (
        <span className={'ml-1 text-xs'}>
          {'('}
          {pct > 0 ? '+' : ''}
          {pct.toFixed(2)}
          {'%)'}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Export `PoolValuationPanel` component.
 *
 * The pool's total value and each contributor's slice, valued at the current
 * market price. Because shares are frozen at contribution time, a member who
 * left (weight 0) keeps the historical value of their participation.
 */

export function PoolValuationPanel({ projectId }: { projectId: string }): JSX.Element | null {
  const [translate] = useTranslation();
  const { data, isLoading } = usePoolInvestValuation(projectId);

  if (isLoading || !data) {
    return null;
  }

  const { totals, participants } = data;

  return (
    <section className={'rounded-xl border border-border bg-card p-5 shadow-sm'}>
      <div className={'mb-4 flex items-center justify-between gap-3'}>
        <h2 className={'text-sm font-semibold uppercase tracking-wide text-muted-foreground'}>
          {translate('poolInvest.valuation.title')}
        </h2>

        {totals.holdings.map(holding => (
          <span className={'text-xs text-muted-foreground'} key={holding.symbol}>
            {fmtQty(holding.quantity)} {holding.symbol}
            {' · '}
            {formatCurrency(holding.priceFiat)}
          </span>
        ))}
      </div>

      {/* Pool totals */}
      <div className={'mb-5 grid grid-cols-2 gap-4 sm:grid-cols-3'}>
        <Stat big label={translate('poolInvest.valuation.currentValue')} value={formatCurrency(totals.currentValue)} />

        <Stat label={translate('poolInvest.valuation.invested')} value={formatCurrency(totals.investedFiat)} />

        <div>
          <div className={'text-[10px] uppercase tracking-wide text-muted-foreground'}>
            {translate('poolInvest.valuation.profit')}
          </div>

          <div className={'text-lg font-semibold'}>
            <Profit pct={totals.profitPct} value={totals.profitFiat} />
          </div>
        </div>
      </div>

      {/* Per-participant breakdown */}
      <div className={'overflow-x-auto'}>
        <table className={'w-full border-collapse text-sm'}>
          <thead>
            <tr
              className={'border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground'}
            >
              <th className={'py-2 pr-3 font-semibold'}>{translate('poolInvest.valuation.participant')}</th>
              <th className={'py-2 px-3 text-right font-semibold'}>{translate('poolInvest.valuation.invested')}</th>
              <th className={'py-2 px-3 text-right font-semibold'}>{translate('poolInvest.valuation.holdings')}</th>
              <th className={'py-2 px-3 text-right font-semibold'}>{translate('poolInvest.valuation.currentValue')}</th>
              <th className={'py-2 pl-3 text-right font-semibold'}>{translate('poolInvest.valuation.profit')}</th>
            </tr>
          </thead>

          <tbody className={'divide-y divide-border'}>
            {participants.map(row => (
              <tr className={'hover:bg-muted/40'} key={row.id}>
                <td className={'py-2 pr-3'}>
                  <span className={'flex items-center gap-2'}>
                    <span className={'font-medium text-foreground'}>{row.name || row.email || '—'}</span>

                    {!row.active && (
                      <Badge className={'text-[10px]'} variant={'secondary'}>
                        {translate('poolInvest.valuation.frozen')}
                      </Badge>
                    )}
                  </span>
                </td>

                <td className={'py-2 px-3 text-right tabular-nums text-muted-foreground'}>
                  {formatCurrency(row.investedFiat)}
                </td>

                <td className={'py-2 px-3 text-right'}>
                  <Holdings holdings={row.holdings} />
                </td>

                <td className={'py-2 px-3 text-right font-medium tabular-nums'}>{formatCurrency(row.currentValue)}</td>

                <td className={'py-2 pl-3 text-right'}>
                  <Profit pct={row.profitPct} value={row.profitFiat} />
                </td>
              </tr>
            ))}
          </tbody>

          <tfoot>
            <tr className={'border-t-2 border-border font-semibold'}>
              <td className={'py-2 pr-3'}>{translate('poolInvest.valuation.total')}</td>

              <td className={'py-2 px-3 text-right tabular-nums'}>{formatCurrency(totals.investedFiat)}</td>

              <td className={'py-2 px-3 text-right'}>
                <Holdings holdings={totals.holdings} />
              </td>

              <td className={'py-2 px-3 text-right tabular-nums'}>{formatCurrency(totals.currentValue)}</td>

              <td className={'py-2 pl-3 text-right'}>
                <Profit pct={totals.profitPct} value={totals.profitFiat} />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

/**
 * A labelled statistic.
 */

function Stat({ label, value, big }: { label: string; value: string; big?: boolean }): JSX.Element {
  return (
    <div>
      <div className={'text-[10px] uppercase tracking-wide text-muted-foreground'}>{label}</div>

      <div className={cn('font-semibold', big ? 'text-xl' : 'text-lg')}>{value}</div>
    </div>
  );
}
